import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  fetchShowcaseP2PBets,
  fetchShowcaseP2PBetById,
  initializeP2PShowcaseListeners,
  selectShowcaseP2PBets,
  selectP2PShowcaseError,
  selectP2PShowcaseFocusError,
  selectP2PShowcaseLoading,
  selectP2PShowcaseLoadingFocus,
  selectP2PShowcaseLoadingMore,
  selectP2PShowcasePagination,
  updateP2PBet,
  clearFocusError,
} from "../../redux/slices/p2pShowcaseSlice";
import { selectAppIsAuthenticated, selectAppUser } from "../redux/appAuthSlice";
import { syncBondChatForUser } from "../redux/bondchat/generalChatSlice";
import P2PIndex from "../components/p2p/pit/P2PIndex";
import P2PDetail from "../components/p2p/pit/P2PDetail";
import P2PFloorOverview from "../components/p2p/pit/P2PFloorOverview";
import {
  getP2PLeagueOptions,
  getP2PStatusCounts,
  groupP2PBetsByLeagueAndFixture,
  hasOpenSpots,
  matchesP2PFilter,
  sortP2PBets,
  getFixtureId,
} from "../utils/p2pHelpers";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";
import { useHorizontalPanelResize } from "../hooks/useHorizontalPanelResize";
import FeedRefreshBanner from "../../components/FeedRefreshBanner";
import { isNetworkOffline } from "../../utils/requireNetwork";
import "../p2p.css";

const FOCUS_QUERY = "w";
const PIT_MOBILE_BREAKPOINT = "(max-width: 900px)";
const PIT_INDEX_DEFAULT = 360;
const PIT_INDEX_MIN = 280;
const PIT_INDEX_MAX = 560;

const P2P = () => {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get(FOCUS_QUERY) || null;

  const bets = useSelector(selectShowcaseP2PBets);
  const isLoading = useSelector(selectP2PShowcaseLoading);
  const isLoadingMore = useSelector(selectP2PShowcaseLoadingMore);
  const isLoadingFocus = useSelector(selectP2PShowcaseLoadingFocus);
  const focusError = useSelector(selectP2PShowcaseFocusError);
  const error = useSelector(selectP2PShowcaseError);
  const pagination = useSelector(selectP2PShowcasePagination);
  const user = useSelector(selectAppUser);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);

  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("recent");
  const [leagueFilter, setLeagueFilter] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [toast, setToast] = useState(null);
  const [expandedFixtures, setExpandedFixtures] = useState(() => new Set());
  const isMobilePit = useMediaQuery(PIT_MOBILE_BREAKPOINT);
  const {
    size: indexWidth,
    setClampedSize: setIndexWidth,
    reset: resetIndexWidth,
    minSize: indexMin,
    maxSize: indexMax,
  } = usePersistedPanelSize("bb-pit-index-w", {
    defaultSize: PIT_INDEX_DEFAULT,
    minSize: PIT_INDEX_MIN,
    maxSize: PIT_INDEX_MAX,
  });
  const onIndexResizePointerDown = useHorizontalPanelResize(
    indexWidth,
    setIndexWidth,
    "start"
  );

  const load = useCallback(
    async (page = 1, append = false, { force = false } = {}) => {
      if (!force && !append && isNetworkOffline()) return;

      const hasCachedFeed = bets.length > 0;
      if (!append && page === 1) {
        setIsUpdating(true);
        if (!hasCachedFeed) setRefreshFailed(false);
      }

      try {
        await dispatch(
          fetchShowcaseP2PBets({
            page,
            limit: 30,
            append,
            force,
            preserveIds: selectedId ? [selectedId] : [],
          })
        ).unwrap();
        if (!append && page === 1) setRefreshFailed(false);
      } catch {
        if (!append && page === 1 && hasCachedFeed) setRefreshFailed(true);
      } finally {
        if (!append && page === 1) setIsUpdating(false);
      }
    },
    [dispatch, bets.length, selectedId]
  );

  useEffect(() => {
    load(1, false);
  }, [load]);

  useEffect(() => {
    if (user?._id) {
      dispatch(syncBondChatForUser(user._id));
      initializeP2PShowcaseListeners(dispatch);
    }
  }, [user?._id, dispatch]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const baseFiltered = useMemo(
    () =>
      bets.filter((b) =>
        matchesP2PFilter(b, filter, user?._id, {
          followingIds: user?.following,
          favoriteTeam: user?.favoriteTeam,
        })
      ),
    [bets, filter, user?._id, user?.following, user?.favoriteTeam]
  );

  const leagueOptions = useMemo(
    () => getP2PLeagueOptions(baseFiltered, 30),
    [baseFiltered]
  );

  const statusCounts = useMemo(() => getP2PStatusCounts(bets), [bets]);

  const filtered = useMemo(() => {
    let list = baseFiltered;
    if (leagueFilter) {
      list = list.filter((b) => {
        const league = b.fixture?.league;
        const id = league?._id || league?.id || league?.name;
        return id != null && String(id) === leagueFilter;
      });
    }
    if (openOnly) {
      list = list.filter(hasOpenSpots);
    }
    return sortP2PBets(list, sortBy);
  }, [baseFiltered, leagueFilter, openOnly, sortBy]);

  const boardSections = useMemo(
    () => groupP2PBetsByLeagueAndFixture(filtered),
    [filtered]
  );

  const selectedFixtureId = useMemo(() => {
    if (!selectedId) return null;
    const bet = filtered.find((b) => b._id === selectedId);
    return bet ? getFixtureId(bet) : null;
  }, [filtered, selectedId]);

  // Clear stale focus errors when the shared id changes.
  useEffect(() => {
    dispatch(clearFocusError());
  }, [selectedId, dispatch]);

  // Shared `?w=` links fetch by id when the duel is not in the current feed page.
  useEffect(() => {
    if (!selectedId) return;
    const inFeed = bets.some((b) => String(b._id) === String(selectedId));
    if (inFeed || isLoading || isLoadingFocus || focusError) return;
    dispatch(fetchShowcaseP2PBetById(selectedId));
  }, [
    bets,
    selectedId,
    isLoading,
    isLoadingFocus,
    focusError,
    dispatch,
  ]);

  // Keep the fixture that holds the selected offer expanded on the board.
  useEffect(() => {
    if (!selectedFixtureId) return;
    setExpandedFixtures((prev) => {
      if (prev.has(selectedFixtureId)) return prev;
      const next = new Set(prev);
      next.add(selectedFixtureId);
      return next;
    });
  }, [selectedFixtureId]);

  const onToggleFixture = useCallback(
    (fixtureId) => {
      setExpandedFixtures((prev) => {
        const next = new Set(prev);
        if (next.has(fixtureId)) {
          next.delete(fixtureId);
          return next;
        }
        next.add(fixtureId);
        return next;
      });
    },
    []
  );

  const selectedBet = useMemo(() => {
    if (!selectedId) return null;
    return bets.find((b) => b._id === selectedId) || null;
  }, [bets, selectedId]);

  const siblingStances = useMemo(() => {
    if (!selectedBet) return [];
    const fid = getFixtureId(selectedBet);
    if (!fid) return [];
    return bets
      .filter((b) => b._id !== selectedBet._id && getFixtureId(b) === fid)
      .slice(0, 6);
  }, [bets, selectedBet]);

  const onSelect = useCallback(
    (id) => {
      const np = new URLSearchParams(params);
      if (id) np.set(FOCUS_QUERY, id);
      else np.delete(FOCUS_QUERY);
      setParams(np, { replace: true });
    },
    [params, setParams]
  );

  const onLeagueChange = useCallback(
    (nextLeagueId) => {
      setLeagueFilter(nextLeagueId);
      if (isMobilePit) {
        onSelect(null);
      }
    },
    [isMobilePit, onSelect]
  );

  const onBackToList = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load(1, false, { force: true });
    setRefreshing(false);
  }, [load]);

  const onLoadMore = useCallback(() => {
    if (!pagination.hasNextPage || isLoadingMore) return;
    load(pagination.page + 1, true);
  }, [pagination, isLoadingMore, load]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea") return;
      if (e.key !== "j" && e.key !== "k" && e.key !== "ArrowDown" && e.key !== "ArrowUp") {
        return;
      }
      if (!filtered.length) return;
      e.preventDefault();
      const idx = filtered.findIndex((b) => b._id === selectedId);
      const next =
        e.key === "j" || e.key === "ArrowDown"
          ? Math.min(filtered.length - 1, idx < 0 ? 0 : idx + 1)
          : Math.max(0, idx <= 0 ? 0 : idx - 1);
      const id = filtered[next]?._id;
      if (id) onSelect(id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filtered, selectedId, onSelect]);

  const shellClassName = [
    "pit-shell",
    isMobilePit ? "pit-shell-mobile" : "",
    isMobilePit && selectedId ? "pit-shell-has-detail" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const pitGridStyle = !isMobilePit
    ? { "--pit-index-w": `${indexWidth}px` }
    : undefined;

  return (
    <div className={shellClassName}>
      <FeedRefreshBanner
        isUpdating={isUpdating}
        refreshFailed={refreshFailed}
        onRetry={() => load(1, false, { force: true })}
      />
      {toast && (
        <div
          className={`pit-toast pit-toast-${toast.type || "success"}`}
          role="status"
        >
          {toast.message}
        </div>
      )}

      {error && !isLoading && bets.length === 0 && (
        <div className="pit-error" role="alert">
          Could not load P2P wagers.{" "}
          <button type="button" onClick={() => load(1, false)}>
            Retry
          </button>
        </div>
      )}

      <div className="pit-grid" style={pitGridStyle}>
        <div className="pit-index-pane">
          <P2PIndex
            bets={filtered}
            boardSections={boardSections}
            expandedFixtures={expandedFixtures}
            onToggleFixture={onToggleFixture}
            selectedId={selectedId}
            onSelect={onSelect}
            filter={filter}
            onFilterChange={setFilter}
            isAuthenticated={isAuthenticated}
            statusCounts={statusCounts}
            sortBy={sortBy}
            onSortChange={setSortBy}
            leagueOptions={leagueOptions}
            leagueFilter={leagueFilter}
            onLeagueChange={onLeagueChange}
            openOnly={openOnly}
            onOpenOnlyChange={setOpenOnly}
            onRefresh={onRefresh}
            refreshing={refreshing}
            isLoading={isLoading}
            totalCount={pagination.totalCount || bets.length}
            filteredCount={filtered.length}
            leagueRailTotal={baseFiltered.length}
            favoriteTeam={user?.favoriteTeam}
            hasMore={pagination.hasNextPage}
            onLoadMore={onLoadMore}
            loadingMore={isLoadingMore}
          />
          {!isMobilePit && (
            <div
              className="pit-resize-handle pit-resize-handle-index"
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={indexWidth}
              aria-valuemin={indexMin}
              aria-valuemax={indexMax}
              aria-label="Resize P2P board"
              title="Drag to resize · double-click to reset"
              onPointerDown={onIndexResizePointerDown}
              onDoubleClick={resetIndexWidth}
            />
          )}
        </div>
        {selectedBet ? (
          <P2PDetail
            bet={selectedBet}
            siblingStances={siblingStances}
            onSelectStance={onSelect}
            onBackToList={isMobilePit ? onBackToList : undefined}
            onBackToFloor={!isMobilePit ? onBackToList : undefined}
            userId={user?._id}
            onNotify={setToast}
            onRefresh={() => load(1, false)}
            onUpdated={(updated) => {
              if (updated?._id) dispatch(updateP2PBet(updated));
            }}
            isAuthenticated={isAuthenticated}
          />
        ) : selectedId && (isLoadingFocus || isLoading) ? (
          <section className="pit-detail pit-detail-idle" aria-busy="true">
            <div className="pit-idle">
              <p className="pit-idle-eyebrow">BondP2P · Shared link</p>
              <p className="pit-idle-title">Opening duel…</p>
              <p className="pit-idle-copy">Loading this P2P wager.</p>
            </div>
          </section>
        ) : selectedId && focusError ? (
          <section className="pit-detail pit-detail-idle" role="alert">
            <div className="pit-idle">
              <p className="pit-idle-eyebrow">BondP2P · Shared link</p>
              <p className="pit-idle-title">Duel not found</p>
              <p className="pit-idle-copy">
                This P2P link is invalid or the wager is no longer available.
              </p>
              <button
                type="button"
                className="pit-idle-cta"
                onClick={() => onSelect(null)}
              >
                Browse open P2P
              </button>
            </div>
          </section>
        ) : (
          <P2PFloorOverview
            bets={filtered}
            boardSections={boardSections}
            onSelectOffer={onSelect}
            isAuthenticated={isAuthenticated}
          />
        )}
      </div>
    </div>
  );
};

export default P2P;
